import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Users, Briefcase, Star, CheckCircle2, ArrowRight } from 'lucide-react';

// Vehicle images - User provided taxi photos
const FLEET_IMAGES = {
  sedan: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/q4ct6q3p_image.png",
  premium_sedan: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/24mvvbgz_image.png",
  suv: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/pov05qzs_image.png",
  premium_suv: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/kh7gk9ht_lexus-suv-1.jpg",
  maxi: "https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/8ly5dt5z_image.png",
};

const FleetPage = () => {
  const navigate = useNavigate();

  const handleBookClick = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const bookingSection = document.getElementById('booking');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const vehicles = [
    {
      name: "Sedan Taxi",
      image: FLEET_IMAGES.sedan,
      passengers: "1-4",
      luggage: "2 large, 2 small",
      description: "Our standard sedan taxis are perfect for everyday travel around Melbourne. Comfortable, reliable, and economical – ideal for airport runs, city trips, and suburban journeys.",
      features: ["Comfortable seating for 4", "Air conditioning", "Ample boot space", "Professional drivers"],
      ideal: "Daily commutes, airport transfers, city travel"
    },
    {
      name: "Premium Sedan",
      image: FLEET_IMAGES.premium_sedan,
      passengers: "1-4",
      luggage: "3 large",
      description: "Experience luxury travel with our premium sedan fleet featuring Mercedes-Benz and Lexus vehicles. Perfect for business executives, special occasions, and those who appreciate refined comfort.",
      features: ["Luxury leather interior", "Executive class service", "Complimentary water", "USB charging ports"],
      ideal: "Business travel, VIP transfers, special occasions"
    },
    {
      name: "SUV Taxi",
      image: FLEET_IMAGES.suv,
      passengers: "1-6",
      luggage: "4 large, 2 small",
      description: "Need extra space? Our SUV taxis offer more legroom and significantly more luggage capacity. Great for families, travelers with extra bags, or anyone who prefers a bit more room.",
      features: ["Extra legroom", "Large cargo space", "Higher seating position", "Comfortable for tall passengers"],
      ideal: "Families, travelers with luggage, group outings"
    },
    {
      name: "Premium SUV",
      image: FLEET_IMAGES.premium_suv,
      passengers: "1-6",
      luggage: "5 large",
      description: "The ultimate in space and luxury combined. Our premium SUV fleet includes Lexus RX and similar vehicles, offering the perfect blend of comfort, space, and sophistication.",
      features: ["Premium leather interior", "Spacious cabin", "Executive service", "Climate controlled"],
      ideal: "Executive groups, luxury airport transfers, weddings"
    },
    {
      name: "Maxi Taxi",
      image: FLEET_IMAGES.maxi,
      passengers: "1-11",
      luggage: "8+ large",
      description: "Traveling with a larger group? Our Maxi Taxis comfortably seat up to 11 passengers with their luggage. Mercedes-Benz Sprinters and similar vehicles ensure everyone travels together in comfort.",
      features: ["Seats up to 11 passengers", "Massive luggage capacity", "Group travel made easy", "Perfect for events"],
      ideal: "Group transfers, sports teams, conference delegates"
    },
  ];

  return (
    <div data-testid="fleet-page">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Our Vehicles</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold mt-2 tracking-tight">
              Our Fleet
            </h1>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto text-lg">
              From comfortable sedans to spacious maxi taxis, we have the perfect vehicle for every journey. 
              All vehicles are meticulously maintained and driven by professional, accredited drivers.
            </p>
          </div>
        </div>
      </section>

      {/* Fleet Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="space-y-16">
            {vehicles.map((vehicle, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src={vehicle.image} 
                      alt={vehicle.name}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                    />
                    <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold">
                      <Users className="w-4 h-4 inline mr-1" />
                      {vehicle.passengers} passengers
                    </div>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <span className="text-xs font-bold uppercase tracking-widest text-primary">
                    {vehicle.luggage} luggage
                  </span>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mt-2 mb-4">
                    {vehicle.name}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {vehicle.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {vehicle.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm bg-orange-50 text-primary px-4 py-2 rounded-lg inline-flex mb-6">
                    <Star className="w-4 h-4" />
                    <span className="font-medium">Ideal for:</span>
                    <span>{vehicle.ideal}</span>
                  </div>

                  <div>
                    <button 
                      onClick={handleBookClick}
                      className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                    >
                      Book {vehicle.name}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-black mb-4">
            Not Sure Which Vehicle You Need?
          </h2>
          <p className="text-black/80 mb-8 max-w-2xl mx-auto">
            Our booking team can recommend the best vehicle for your journey based on passenger numbers, luggage requirements, and travel preferences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:1300096685"
              className="bg-secondary hover:bg-secondary/90 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Call 1300 096 685
            </a>
            <Link 
              to="/contact"
              className="bg-white hover:bg-gray-100 text-secondary px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FleetPage;
